import { useState, useRef, useEffect } from 'react';

interface OutlineSlide {
  number: number;
  title: string;
  type: string;
  icon: string;
  bullets: string[];
  duration: string;
}

interface GenerateAIPromptBarProps {
  onUseOutline: () => void;
}

const TONE_OPTIONS = ['Professional', 'Casual', 'Academic', 'Persuasive', 'Inspiring'];
const SLIDE_COUNT_OPTIONS = [8, 12, 16, 20, 24];
const AUDIENCE_OPTIONS = ['General Public', 'Executives', 'Investors', 'Students', 'Technical Team', 'Clients'];

function generateOutlineFromTopic(topic: string, slides: number): OutlineSlide[] {
  const types = [
    { type: 'Title', icon: 'ri-home-3-line', duration: '1 min' },
    { type: 'Agenda', icon: 'ri-list-check-2', duration: '1 min' },
    { type: 'Problem Statement', icon: 'ri-error-warning-line', duration: '2 min' },
    { type: 'Overview', icon: 'ri-eye-line', duration: '2 min' },
    { type: 'Key Insight', icon: 'ri-lightbulb-flash-line', duration: '3 min' },
    { type: 'Data & Evidence', icon: 'ri-bar-chart-box-line', duration: '3 min' },
    { type: 'Case Study', icon: 'ri-briefcase-4-line', duration: '3 min' },
    { type: 'Solution / Approach', icon: 'ri-tools-line', duration: '3 min' },
    { type: 'Process Flow', icon: 'ri-flow-chart', duration: '2 min' },
    { type: 'Benefits', icon: 'ri-star-line', duration: '2 min' },
    { type: 'Roadmap', icon: 'ri-road-map-line', duration: '2 min' },
    { type: 'Comparison', icon: 'ri-scales-3-line', duration: '2 min' },
    { type: 'Team', icon: 'ri-team-line', duration: '2 min' },
    { type: 'Next Steps', icon: 'ri-arrow-right-circle-line', duration: '2 min' },
    { type: 'Q&A / Closing', icon: 'ri-question-answer-line', duration: '5 min' },
    { type: 'Call to Action', icon: 'ri-megaphone-line', duration: '2 min' },
    { type: 'Thank You', icon: 'ri-hand-heart-line', duration: '1 min' },
    { type: 'Resources', icon: 'ri-links-line', duration: '1 min' },
    { type: 'Appendix', icon: 'ri-attachment-2', duration: '1 min' },
    { type: 'Contact', icon: 'ri-mail-line', duration: '1 min' },
    { type: 'Quote', icon: 'ri-double-quotes-l', duration: '1 min' },
    { type: 'Statistics', icon: 'ri-pie-chart-2-line', duration: '2 min' },
    { type: 'Timeline', icon: 'ri-time-line', duration: '2 min' },
    { type: 'Summary', icon: 'ri-file-list-3-line', duration: '2 min' },
  ];

  const topicWords = topic.trim().split(/\s+/);
  const mainWord = topicWords[0] ?? 'Your Topic';
  const titleWord = topic.length > 30 ? topic.substring(0, 30) + '...' : topic;

  const bulletsMap: Record<string, string[]> = {
    'Title': [`${titleWord}`, 'Presented by Your Name', new Date().getFullYear().toString()],
    'Agenda': ['Introduction & context', 'Core analysis', 'Key insights', 'Recommendations & next steps'],
    'Problem Statement': [`The challenge with ${mainWord}`, 'Current state vs. desired state', 'Why this matters now'],
    'Overview': [`What is ${mainWord}?`, 'Scope of this presentation', 'What you\'ll learn today'],
    'Key Insight': ['Insight #1: Market dynamics', 'Insight #2: User behavior', 'Insight #3: Opportunity size'],
    'Data & Evidence': ['Supporting data point #1', 'Supporting data point #2', 'Source: Research findings 2025'],
    'Case Study': [`Real-world application of ${mainWord}`, 'Before & after metrics', 'Lessons learned'],
    'Solution / Approach': [`Our approach to ${mainWord}`, 'Methodology overview', 'Why this solution works'],
    'Process Flow': ['Step 1: Discovery', 'Step 2: Implementation', 'Step 3: Measurement & iteration'],
    'Benefits': ['Benefit #1: Efficiency gains', 'Benefit #2: Cost reduction', 'Benefit #3: Competitive edge'],
    'Roadmap': ['Q1: Foundation', 'Q2–Q3: Execution', 'Q4: Scale & optimize'],
    'Comparison': ['Option A vs. Option B', 'Key differentiators', 'Recommendation'],
    'Team': ['Core team members', 'Key advisors', 'Combined expertise'],
    'Next Steps': ['Action item #1', 'Action item #2', 'Timeline for execution'],
    'Q&A / Closing': ['Open floor for questions', 'Key takeaways recap', 'Connect with us'],
    'Call to Action': [`Get started with ${mainWord}`, 'Book a demo / consultation', 'Special offer for today'],
    'Thank You': ['Thank you for your time', 'Contact information', 'Resources & links'],
    'Resources': ['Further reading', 'Tools & templates', 'Useful links'],
    'Appendix': ['Supporting charts', 'Detailed methodology', 'Raw data tables'],
    'Contact': ['Email & phone', 'Website & social', 'Schedule a follow-up'],
    'Quote': [`"${mainWord} is transforming the way we work"`, '— Industry Leader'],
    'Statistics': ['Statistic #1', 'Statistic #2', 'Source attribution'],
    'Timeline': ['Phase 1: Research (Month 1–2)', 'Phase 2: Build (Month 3–5)', 'Phase 3: Launch (Month 6)'],
    'Summary': ['Key finding #1', 'Key finding #2', 'Core recommendation'],
  };

  return Array.from({ length: Math.min(slides, types.length) }, (_, i) => {
    const t = types[i];
    return {
      number: i + 1,
      title: t.type,
      type: t.type,
      icon: t.icon,
      bullets: bulletsMap[t.type] ?? ['Content point 1', 'Content point 2', 'Content point 3'],
      duration: t.duration,
    };
  });
}

export default function GenerateAIPromptBar({ onUseOutline }: GenerateAIPromptBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('Professional');
  const [audience, setAudience] = useState('General Public');
  const [slideCount, setSlideCount] = useState(12);
  const [isGenerating, setIsGenerating] = useState(false);
  const [outline, setOutline] = useState<OutlineSlide[] | null>(null);
  const [progress, setProgress] = useState(0);
  const [showOptions, setShowOptions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        if (!outline) setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [outline]);

  const handleGenerate = () => {
    if (!topic.trim()) { inputRef.current?.focus(); return; }
    setIsGenerating(true);
    setOutline(null);
    setProgress(0);
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) { clearInterval(interval); return 100; }
        return p + (p < 70 ? 8 : p < 90 ? 3 : 1);
      });
    }, 120);
    setTimeout(() => {
      clearInterval(interval);
      setProgress(100);
      setOutline(generateOutlineFromTopic(topic, slideCount));
      setIsGenerating(false);
    }, 2600);
  };

  const handleReset = () => {
    setOutline(null);
    setTopic('');
    setProgress(0);
    setIsExpanded(false);
  };

  const typeColors: Record<string, string> = {
    'Title': 'bg-rose-100 text-rose-700',
    'Agenda': 'bg-slate-100 text-slate-600',
    'Problem Statement': 'bg-orange-100 text-orange-700',
    'Overview': 'bg-sky-100 text-sky-700',
    'Key Insight': 'bg-amber-100 text-amber-700',
    'Data & Evidence': 'bg-indigo-100 text-indigo-700',
    'Case Study': 'bg-emerald-100 text-emerald-700',
    'Solution / Approach': 'bg-green-100 text-green-700',
    'Process Flow': 'bg-cyan-100 text-cyan-700',
    'Benefits': 'bg-yellow-100 text-yellow-700',
    'Roadmap': 'bg-violet-100 text-violet-700',
    'Comparison': 'bg-pink-100 text-pink-700',
    'Team': 'bg-blue-100 text-blue-700',
    'Next Steps': 'bg-teal-100 text-teal-700',
    'Q&A / Closing': 'bg-red-100 text-red-700',
    'Call to Action': 'bg-rose-100 text-rose-700',
    'Thank You': 'bg-fuchsia-100 text-fuchsia-700',
  };

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-full max-w-3xl px-4" ref={panelRef}>
      <div className={`bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-500 ${isExpanded ? 'ring-2 ring-rose-300' : ''}`}>

        {/* ── Outline results panel */}
        {outline && (
          <div className="max-h-[60vh] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            <div className="px-5 pt-4 pb-3 flex items-center justify-between border-b border-slate-100 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-gradient-to-br from-rose-500 to-fuchsia-500 rounded-lg flex items-center justify-center">
                  <i className="ri-sparkling-2-line text-white text-xs"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">AI-Generated Outline</p>
                  <p className="text-xs text-slate-400">&ldquo;{topic}&rdquo; &bull; {outline.length} slides &bull; {tone} &bull; {audience}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onUseOutline}
                  className="px-4 py-1.5 bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white text-xs font-bold rounded-lg hover:opacity-90 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                >
                  <i className="ri-magic-line"></i>Create Presentation
                </button>
                <button onClick={handleReset} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-pointer transition-colors">
                  <i className="ri-close-line"></i>
                </button>
              </div>
            </div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {outline.map((slide) => (
                <div key={slide.number} className="flex gap-3 p-3 rounded-xl border border-slate-100 hover:border-rose-200 transition-colors bg-white group">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${typeColors[slide.type] ?? 'bg-slate-100 text-slate-600'}`}>
                    <i className={`${slide.icon} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-mono text-slate-400">{String(slide.number).padStart(2, '0')}</span>
                      <span className="text-xs font-bold text-slate-800">{slide.title}</span>
                      <span className="ml-auto text-xs text-slate-400 flex-shrink-0">{slide.duration}</span>
                    </div>
                    <ul className="space-y-0.5">
                      {slide.bullets.slice(0, 2).map((b, bi) => (
                        <li key={bi} className="text-xs text-slate-500 truncate flex items-start gap-1">
                          <span className="text-slate-300 mt-0.5">&#x2022;</span>{b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 pb-4 flex gap-2">
              <button
                onClick={() => { setOutline(null); setIsGenerating(false); setProgress(0); }}
                className="flex-1 py-2.5 border-2 border-slate-200 text-slate-600 text-sm font-bold rounded-xl hover:bg-slate-50 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-1.5"></i>Regenerate
              </button>
              <button
                onClick={onUseOutline}
                className="flex-1 py-2.5 bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white text-sm font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-magic-line mr-1.5"></i>Build Presentation
              </button>
            </div>
          </div>
        )}

        {/* ── Generating progress */}
        {isGenerating && (
          <div className="px-5 py-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-800">Building your outline...</p>
                <p className="text-xs text-slate-400">Analyzing topic, structuring {slideCount} slides, applying {tone.toLowerCase()} tone</p>
              </div>
              <span className="text-sm font-bold text-rose-600">{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-rose-500 to-fuchsia-500 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* ── Expanded options row */}
        {isExpanded && !isGenerating && !outline && showOptions && (
          <div className="px-5 py-3 border-b border-slate-100 grid grid-cols-3 gap-3 bg-slate-50/50">
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1.5">Tone</label>
              <div className="flex flex-wrap gap-1">
                {TONE_OPTIONS.map(t => (
                  <button
                    key={t}
                    onClick={() => setTone(t)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-full cursor-pointer transition-all whitespace-nowrap ${
                      tone === t ? 'bg-rose-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-rose-300'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1.5">Audience</label>
              <div className="flex flex-wrap gap-1">
                {AUDIENCE_OPTIONS.map(a => (
                  <button
                    key={a}
                    onClick={() => setAudience(a)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-full cursor-pointer transition-all whitespace-nowrap ${
                      audience === a ? 'bg-rose-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-rose-300'
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1.5">Slide Count</label>
              <div className="flex flex-wrap gap-1">
                {SLIDE_COUNT_OPTIONS.map(n => (
                  <button
                    key={n}
                    onClick={() => setSlideCount(n)}
                    className={`px-2.5 py-1 text-xs font-bold rounded-full cursor-pointer transition-all whitespace-nowrap ${
                      slideCount === n ? 'bg-rose-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-rose-300'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Main input bar */}
        {!isGenerating && !outline && (
          <div className="flex items-center gap-2 px-3 py-2.5">
            <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-rose-500 to-fuchsia-500 rounded-xl flex-shrink-0">
              <i className="ri-sparkling-2-line text-white text-base"></i>
            </div>
            <input
              ref={inputRef}
              type="text"
              value={topic}
              onChange={e => setTopic(e.target.value)}
              onFocus={() => setIsExpanded(true)}
              onKeyDown={e => { if (e.key === 'Enter') handleGenerate(); }}
              placeholder="Describe your presentation topic — AI will build the outline..."
              className="flex-1 text-sm text-slate-900 placeholder-slate-400 bg-transparent outline-none min-w-0"
            />
            {isExpanded && (
              <button
                onClick={() => setShowOptions(s => !s)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  showOptions ? 'bg-rose-100 text-rose-600' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <i className="ri-settings-3-line"></i>
                {showOptions ? 'Hide' : 'Options'}
              </button>
            )}
            {isExpanded && (
              <div className="flex items-center gap-1 text-xs text-slate-400 whitespace-nowrap hidden md:flex">
                <span className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">{slideCount}</span> slides &bull;
                <span className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">{tone}</span>
              </div>
            )}
            <button
              onClick={handleGenerate}
              disabled={!topic.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white text-sm font-bold rounded-xl hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              <i className="ri-magic-line"></i>
              {isExpanded ? 'Generate' : 'Generate Outline'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
